import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import { Clock3, Eye, RefreshCw, Search, Trash2, UserCheck, Users } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { approveContractor, deleteCont, getAllContractors } from "../../../slices/thunk";
import "./real-estate-agents.css";

const pageSize = 10;

function formatCreatedAt(value?: string) {
  if (!value) return { date: "Not available", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: value, time: "" };

  return {
    date: date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }),
    time: date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
}

function displayName(agent: any) {
  const name = [agent.user_first_name, agent.user_last_name].filter(Boolean).join(" ").trim();
  return name || "Unnamed agent";
}

const AdminRealEstateAgents = () => {
  document.title = "Real Estate Agents";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState({ show: false, variant: "", message: "" });

  const allContractors = useSelector((state: any) => state.Admin.contractors);
  const agents = Array.isArray(allContractors) ? allContractors : [];
  const dispatch: any = useDispatch();

  const loadAgents = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    await dispatch(getAllContractors("real_estate_agent"));
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    loadAgents();
  }, []);

  const filteredAgents = useMemo(() => {
    const query = search.trim().toLowerCase();
    return agents.filter((agent: any) => {
      const isActive = Number(agent.is_verified) === 1;
      const matchesStatus = statusFilter === "all" || (statusFilter === "active" ? isActive : !isActive);
      const matchesSearch = !query || [
        agent.id,
        agent.user_first_name,
        agent.user_last_name,
        agent.user_email,
        agent.user_phone,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));

      return matchesStatus && matchesSearch;
    });
  }, [agents, search, statusFilter]);

  const activeCount = agents.filter((agent: any) => Number(agent.is_verified) === 1).length;
  const pendingCount = Math.max(agents.length - activeCount, 0);
  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / pageSize));
  const visibleAgents = filteredAgents.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const rangeStart = filteredAgents.length ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, filteredAgents.length);

  const updateFilters = (callback: () => void) => {
    callback();
    setCurrentPage(1);
  };

  const handleSwitchToggle = async (agent: any, isChecked: boolean) => {
    setUpdatingId(agent.id);
    await dispatch(approveContractor(isChecked, agent.id));
    await dispatch(getAllContractors("real_estate_agent"));
    setUpdatingId(null);
    setNotification({
      show: true,
      variant: "success",
      message: `${displayName(agent)} is now ${isChecked ? "active" : "inactive"}.`,
    });
  };

  const openDeleteModal = (agent: any) => {
    setSelectedAgent(agent);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedAgent?.id) return;
    setDeleting(true);
    const response = await dispatch(deleteCont(selectedAgent.id));
    if (response) {
      setNotification({ show: true, variant: "success", message: response.message || "Agent deleted successfully." });
      await dispatch(getAllContractors("real_estate_agent"));
      setDeleteModalOpen(false);
      setSelectedAgent(null);
    }
    setDeleting(false);
  };

  return (
    <React.Fragment>
      <div className="page-content real-estate-agents-page">
        <Container fluid className="real-estate-agents-shell">
          {notification.show ? (
            <Alert
              variant={notification.variant}
              onClose={() => setNotification({ show: false, variant: "", message: "" })}
              dismissible
              className="mb-0"
            >
              {notification.message}
            </Alert>
          ) : null}

          <div className="real-estate-agent-stats">
            <Card className="agent-stat-card">
              <Card.Body>
                <span className="agent-stat-icon"><Users size={20} /></span>
                <div><span>Total agents</span><strong>{agents.length.toLocaleString()}</strong></div>
              </Card.Body>
            </Card>
            <Card className="agent-stat-card">
              <Card.Body>
                <span className="agent-stat-icon"><UserCheck size={20} /></span>
                <div><span>Active agents</span><strong>{activeCount.toLocaleString()}</strong></div>
              </Card.Body>
            </Card>
            <Card className="agent-stat-card agent-stat-pending">
              <Card.Body>
                <span className="agent-stat-icon"><Clock3 size={20} /></span>
                <div><span>Inactive agents</span><strong>{pendingCount.toLocaleString()}</strong></div>
              </Card.Body>
            </Card>
          </div>

          <Card className="real-estate-agents-card">
            <Card.Header>
              <div className="agents-toolbar">
                <div>
                  <h5 className="mb-1">Real Estate Agents</h5>
                  <p>Manage agent access and view account information.</p>
                </div>
                <div className="agents-toolbar-controls">
                  <div className="agents-search">
                    <Search size={16} aria-hidden="true" />
                    <Form.Control
                      value={search}
                      onChange={(event) => updateFilters(() => setSearch(event.target.value))}
                      placeholder="Search agents"
                      aria-label="Search real estate agents"
                    />
                  </div>
                  <Form.Select
                    value={statusFilter}
                    onChange={(event) => updateFilters(() => setStatusFilter(event.target.value))}
                    aria-label="Filter agents by status"
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                  <Button variant="outline-primary" className="agents-refresh" onClick={() => loadAgents(true)} disabled={refreshing}>
                    {refreshing ? <Spinner size="sm" /> : <RefreshCw size={16} />} Refresh
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0 real-estate-agents-table">
                  <colgroup>
                    <col className="agent-col-agent" />
                    <col className="agent-col-contact" />
                    <col className="agent-col-created" />
                    <col className="agent-col-status" />
                    <col className="agent-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Contact</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={5} className="agents-table-state"><Spinner size="sm" /> Loading agents...</td></tr>
                    ) : visibleAgents.length ? (
                      visibleAgents.map((agent: any) => {
                        const createdAt = formatCreatedAt(agent.created_at);
                        const isActive = Number(agent.is_verified) === 1;
                        return (
                          <tr key={agent.id}>
                            <td className="agent-cell-identity">
                              <div className="agent-name">{displayName(agent)}</div>
                              <div className="agent-secondary">Agent ID #{agent.id}</div>
                            </td>
                            <td className="agent-cell-contact">
                              <a className="agent-contact" href={agent.user_email ? `mailto:${agent.user_email}` : undefined}>{agent.user_email || "No email"}</a>
                              <a className="agent-secondary agent-phone" href={agent.user_phone ? `tel:${agent.user_phone}` : undefined}>{agent.user_phone || "No phone"}</a>
                            </td>
                            <td className="agent-cell-created">
                              <div className="agent-created-date">{createdAt.date}</div>
                              {createdAt.time ? <div className="agent-secondary">{createdAt.time}</div> : null}
                            </td>
                            <td className="agent-cell-status">
                              <div className="agent-status-control">
                                <span className={`agent-status ${isActive ? "is-active" : "is-inactive"}`}>{isActive ? "Active" : "Inactive"}</span>
                                {updatingId === agent.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <Form.Check
                                    type="switch"
                                    id={`agent-status-${agent.id}`}
                                    checked={isActive}
                                    onChange={(event) => handleSwitchToggle(agent, event.target.checked)}
                                    aria-label={`Set ${displayName(agent)} ${isActive ? "inactive" : "active"}`}
                                  />
                                )}
                              </div>
                            </td>
                            <td className="agent-cell-actions">
                              <div className="agent-row-actions">
                                <Link to={`/pages-single-contractors?id=${agent.id}`} className="btn btn-primary btn-sm">
                                  <Eye size={14} /> View
                                </Link>
                                <Button size="sm" variant="outline-danger" onClick={() => openDeleteModal(agent)} aria-label={`Delete ${displayName(agent)}`}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan={5} className="agents-table-state">No agents match the selected filters.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>

            {!loading && filteredAgents.length > 0 ? (
              <Card.Footer className="agents-pagination">
                <span>Showing {rangeStart}–{rangeEnd} of {filteredAgents.length.toLocaleString()}</span>
                <div>
                  <Button size="sm" variant="outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</Button>
                  <span>Page {currentPage} of {totalPages}</span>
                  <Button size="sm" variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>Next</Button>
                </div>
              </Card.Footer>
            ) : null}
          </Card>
        </Container>
      </div>

      <Modal show={deleteModalOpen} onHide={() => !deleting && setDeleteModalOpen(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete agent?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">You’re about to permanently delete <strong>{selectedAgent ? displayName(selectedAgent) : "this agent"}</strong>.</p>
          <p className="text-muted mb-0">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" className="me-2" /> : <Trash2 size={15} className="me-2" />}
            Delete agent
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default AdminRealEstateAgents;
